/**
 * The things in the property you can press E at.
 *
 * Each one points at a prop in `level.js` by id rather than carrying its own
 * coordinates. That is the whole reason this file is separate: moving the BEO
 * board six feet down the alcove wall is an edit to the blockout, and the thing
 * you interact with should follow it rather than quietly stay where it was.
 *
 * `reach` is in feet, measured from the prop's edge, and it is generous. A
 * third person camera makes it genuinely hard to judge your own distance to a
 * wall-mounted object, and an interaction you have to line up twice is an
 * interaction that feels broken rather than precise.
 */
import { PICKUPS } from './resources.js';

/**
 * Stations: where another department's stock physically sits.
 *
 * The reason these exist is that "take it, free unless somebody sees you"
 * cannot happen across a counter from the person you are taking it from — they
 * are three feet away and looking at you. Taking is something you do at the
 * station, and whether you get away with it depends entirely on who is on the
 * floor. Which makes the availability windows in `staff.js` into a schedule
 * worth learning: the Housekeeping Lead goes home at four, and after four her
 * carts are just carts.
 *
 * Asking and trading still happen with the person, because there is nobody to
 * ask when nobody is there.
 */
/**
 * Where a resolved shortfall put the thing.
 *
 * Derived from `resources.js` rather than written out, so a shortfall cannot
 * name a place and then have nowhere to collect it from — and `interaction.js`
 * already throws at boot if any of these points at a prop that is not in the
 * blockout, which makes this list its own audit.
 *
 * One per place rather than one per shortfall, because where a thing lands is
 * read off the rung you are on with whoever was holding it: the linen is on
 * their cart or it is in the room, and both of those are somewhere you might
 * have to walk to. The rungs where it has already *arrived* are not in here —
 * there is nothing piled on a shelf you were walking to anyway, and the three
 * supply points already have a prompt of their own.
 *
 * The two access shortfalls name the thing that is locked, and there is nothing
 * to pick up off a padlock.
 */
export const DELIVERIES = PICKUPS.map((pickup) => ({
  id: `delivery-${pickup.place}`,
  prop: pickup.place,
  kind: 'delivery',
  shortfall: pickup.shortfall.id,
  supplies: pickup.shortfall.item,
  label: pickup.spot,
  reach: 5,
}));

export const STATIONS = [
  { id: 'station-housekeeping', prop: 'hskp-cart-b', department: 'housekeeping', label: 'Housekeeping carts', reach: 5 },
  { id: 'station-kitchen', prop: 'kitchen-pass', department: 'kitchen', label: 'Kitchen pass', reach: 5 },
  { id: 'station-engineering', prop: 'shop-shelves', department: 'engineering', label: 'Engineering shelves', reach: 5 },
  { id: 'station-valet', prop: 'dock-crate', department: 'valet', label: 'Dock crate', reach: 5 },
  { id: 'station-frontdesk', prop: 'lobby-desk', department: 'frontdesk', label: 'Front desk', reach: 5 },
].map((station) => ({ ...station, kind: 'station' }));

export const INTERACTABLES = [
  {
    id: 'time-clock',
    prop: 'time-clock',
    kind: 'timeclock',
    label: 'Time Clock',
    reach: 5,
  },
  {
    /**
     * Your locker. A clean uniform, which is the only thing that takes a smell
     * off one — waiting does not, and neither does the end of the shift.
     */
    id: 'lockers',
    prop: 'break-lockers',
    kind: 'lockers',
    label: 'Lockers',
    reach: 5,
  },
  {
    id: 'beo-board',
    prop: 'beo-board',
    kind: 'beoboard',
    label: 'BEO Board',
    reach: 5,
  },
  {
    id: 'cot',
    prop: 'break-cot',
    kind: 'endday',
    label: 'Cot',
    reach: 4,
  },
  {
    id: 'store-rounds',
    prop: 'store-rounds',
    kind: 'supply',
    supplies: 'round',
    label: '72" Rounds',
    reach: 5,
  },
  {
    id: 'store-chairs',
    prop: 'store-chairs',
    kind: 'supply',
    supplies: 'chair',
    label: 'Banquet Chairs',
    reach: 5,
  },
  {
    id: 'store-linen',
    prop: 'store-linen',
    kind: 'supply',
    supplies: 'linen',
    label: 'Linens',
    reach: 5,
  },
  ...STATIONS,
  ...DELIVERIES,
];

export default INTERACTABLES;
